/* eslint-disable no-underscore-dangle */
import "./index.scss";
import { Button } from "@wise-emotions/react-button";
import {
  GeneralMediumClose,
  GeneralMediumProfile,
  GeneralMediumSearch,
} from "@wise-emotions/react-icons";
import { logDebug } from "helpers/generic";
import { useMenu } from "utilities/MenuProvider";
import IconWrap from "components/Icon/IconWrap";
import Image from "components/Image/CfImage";
import Menu from "components/Menu";
import MenuTop from "components/Menu/MenuDefault/MenuTop";
import React, { useCallback, useEffect, useRef } from "react";
import ScrollMagic, { Controller, Scene, UpdateEvent } from "scrollmagic";
// eslint-disable-next-line no-use-before-define
// import "debug.addIndicators";
import { CfMenu } from "components/Menu/types";
import { Langs } from "types/types";
import SearchOverlay from "./searchOverlay";
import cn from "classnames";
import dictionary from "configs/dictionary";

const ComponentLink = dictionary.ContentfulCtaReferenceV2 as React.ElementType;

export interface LangProp {
  current?: boolean;
  internalLink: {
    node_locale: Langs;
    slug: string;
  };
  label: string;
  ctaType: string;
  link: string;
}

export const LangsUi = () => {
  const menuHelpers = useMenu();
  const { langs } = menuHelpers;

  return (
    <div className="langs">
      <div className="langs__list">
        {langs.map((lang: LangProp) => {
          const langItemClass = cn("langs__item", {
            "langs__item--current": lang.current,
          });

          return (
            <div className={langItemClass} key={lang.label}>
              <ComponentLink
                ctaType={lang.ctaType}
                internalLink={lang.internalLink}
                className="langs__link"
              >
                {lang.label}
              </ComponentLink>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface HeaderProps {
  data: CfMenu;
  // clickSearch?: (e: React.MouseEvent) => ();
  clickSearch?: VoidFunction;
  searchContent?: React.ReactNode;
}

const Header = ({ data, clickSearch, searchContent }: HeaderProps) => {
  const menuHelpers = useMenu();

  const { toggleMenu, backMenuMobile, privateArea, toggleSearch, searchOpen } =
    menuHelpers;

  const header = useRef<HTMLDivElement>(null);
  const controller = useRef<Controller>();
  const sceneSticky = useRef<Scene>();

  const { settings } = data;

  logDebug("settings menu", settings);
  logDebug("settings data", data);

  const hasMenuTop = data.content[0].__typename === "ContentfulMenuTopV2";

  const manageMagicMenu = (toBottom: boolean) => {
    if (!header.current) {
      return;
    }
    if (header.current.classList.contains("header-magic") && toBottom) {
      header.current.classList.add("header-magic--active");
    } else {
      header.current.classList.remove("header-magic--active");
    }
  };

  const initScroll = useCallback(() => {
    if (!header.current) {
      return false;
    }
    controller.current = new ScrollMagic.Controller();
    sceneSticky.current = new ScrollMagic.Scene({
      offset: 200,
    })
      .setClassToggle(header.current, "header-magic")
      .addTo(controller.current);

    let prevPosition = 0;
    let timeoutMagic: NodeJS.Timeout;
    sceneSticky.current.on("update", (event: UpdateEvent) => {
      if (timeoutMagic) {
        clearTimeout(timeoutMagic);
      }
      timeoutMagic = setTimeout(() => {
        if (prevPosition > event.scrollPos || event.scrollPos < 200) {
          manageMagicMenu(false);
        } else {
          manageMagicMenu(true);
        }
        prevPosition = event.scrollPos;
      }, 200);
    });
    return () => clearTimeout(timeoutMagic);
  }, []);

  const destroyController = () => {
    if (!controller.current) return;
    controller.current.destroy(true);
  };

  useEffect(() => {
    initScroll();
    return () => {
      destroyController();
    };
  }, [initScroll]);

  const onClickSearch = (e: React.MouseEvent) => {
    if (clickSearch) {
      // @ts-expect-error
      clickSearch(e, toggleSearch);
    } else if (toggleSearch) {
      toggleSearch();
    }
  };

  return (
    <>
      <header className="header stickyOnTop" ref={header}>
        <div className="header__top-desktop">
          {hasMenuTop && <MenuTop data={data} />}
        </div>
        <div className="header__content">
          <button
            // TODO: add correct aria label ( not hardcoded value )
            aria-label="Open menu"
            type="button"
            className="header__toggle"
            onClick={(e) => {
              return toggleMenu!(e.target as HTMLButtonElement);
            }}
          >
            <div className="header__toggle__icon">
              <span />
              <span />
              <span />
              <span />
            </div>
          </button>
          <div className="header__logo">
            {settings?.logo[0] && (
              <ComponentLink {...settings.logo[0]}>
                <Image {...settings.logo[0].content[0]} />
              </ComponentLink>
            )}
          </div>
          <div className="header__nav">
            <div className="header__menu">
              <Menu data={data} />
            </div>
          </div>
          <div className="header__util">
            <div className="header__util__item">
              <div className="header__search">
                {searchContent && (
                  <div className="search">
                    <button
                      className="search__icon header__util__icon"
                      onClick={onClickSearch}
                      type="button"
                      data-testid="btn-search-menu"
                      // TODO: static for now we change when migrate inside site
                      aria-label={searchOpen ? "open search" : "close search"}
                    >
                      {searchOpen ? (
                        <IconWrap>
                          <GeneralMediumClose />
                        </IconWrap>
                      ) : (
                        <IconWrap>
                          <GeneralMediumSearch />
                        </IconWrap>
                      )}
                    </button>
                  </div>
                )}
              </div>
              <div className="header__langs">
                <LangsUi />
              </div>
            </div>
            {privateArea && (
              <div className="header__util__item ">
                <div className="header__profile">
                  <div className="profile">
                    {/* TODO: login */}
                    {/* eslint-disable-next-line no-constant-condition */}
                    {true ? (
                      <div className="search__privateArea">
                        <ComponentLink {...privateArea}>
                          <div className="profile__btn-login">
                            <Button variant="default" size="md">
                              {privateArea?.content[0]?.label}
                            </Button>
                          </div>
                          <div className="profile__icon header__util__icon">
                            <GeneralMediumProfile />
                          </div>
                        </ComponentLink>
                      </div>
                    ) : (
                      <div className="profile__user">
                        <ComponentLink {...privateArea}>
                          <div className="profile__user__img header__util__icon">
                            <img
                              src="https://placeimg.com/50/50/people"
                              alt=""
                            />
                          </div>
                          <div className="profile__user__name">Nome Utente</div>
                        </ComponentLink>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
      {searchContent && (
        <SearchOverlay isOpen={searchOpen}>{searchContent}</SearchOverlay>
      )}
    </>
  );
};

export default Header;
